import { useEffect, useState } from 'react';
import { Plus, Download, Mail } from 'lucide-react';
import api from '../lib/api';
import Header from '../components/Layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import useAuthStore from '../store/useAuthStore';

const Invoices = () => {
  const { user } = useAuthStore();
  const canManageInvoices = user?.role === 'admin' || user?.role === 'editor';
  
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    project_id: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    amount: '',
    service_period: '',
    status: 'Unpaid',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [invoicesRes, projectsRes] = await Promise.all([
        api.get('/invoices'),
        api.get('/projects'),
      ]);
      setInvoices(invoicesRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    try {
      await api.post('/invoices', {
        ...formData,
        invoice_date: new Date(formData.invoice_date).toISOString(),
        due_date: new Date(formData.due_date).toISOString(),
        amount: parseFloat(formData.amount),
      });
      toast.success('Invoice created successfully');
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  const downloadInvoicePDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Invoice downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  const getStatusBadge = (status) => {
    const badges = {
      'Paid': 'bg-emerald-50 text-emerald-700 border-emerald-100',
      'Partially Paid': 'bg-amber-50 text-amber-700 border-amber-100',
      'Unpaid': 'bg-red-50 text-red-700 border-red-100',
    };
    return badges[status] || badges.Unpaid;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const totalValue = invoices.reduce((sum, inv) => sum + inv.amount, 0);

  return (
    <div className="min-h-screen">
      <Header title="Invoices" subtitle="Manage and track all project invoices" />

      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
            <p className="text-sm font-medium text-slate-500 mb-1">Total Invoice Value</p>
            <p className="text-3xl font-heading font-bold text-slate-900">{formatCurrency(totalValue)}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
            <p className="text-sm font-medium text-slate-500 mb-1">Total Invoices</p>
            <p className="text-3xl font-heading font-bold text-slate-900">{invoices.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
            <p className="text-sm font-medium text-slate-500 mb-1">Unpaid</p>
            <p className="text-3xl font-heading font-bold text-red-600">
              {invoices.filter(i => i.status === 'Unpaid').length}
            </p>
          </div>
        </div>

        {canManageInvoices && (
          <div className="flex justify-end mb-6">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-orange-500 hover:bg-orange-600 text-white">
                  <Plus className="w-5 h-5 mr-2" />
                  Create Invoice
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create New Invoice</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateInvoice} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Project</Label>
                      <Select value={formData.project_id} onValueChange={(v) => setFormData({...formData, project_id: v})} required>
                        <SelectTrigger className="bg-slate-50">
                          <SelectValue placeholder="Select project" />
                        </SelectTrigger>
                        <SelectContent>
                          {projects.map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Invoice Number</Label>
                      <Input
                        placeholder="INV-24-25-001"
                        value={formData.invoice_number}
                        onChange={(e) => setFormData({...formData, invoice_number: e.target.value})}
                        required
                        className="bg-slate-50"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Invoice Date</Label>
                      <Input
                        type="date"
                        value={formData.invoice_date}
                        onChange={(e) => setFormData({...formData, invoice_date: e.target.value})}
                        required
                        className="bg-slate-50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Due Date</Label>
                      <Input
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                        required
                        className="bg-slate-50"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Amount (INR)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) => setFormData({...formData, amount: e.target.value})}
                        required
                        className="bg-slate-50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Service Period</Label>
                      <Input
                        placeholder="10 Mar - 10 Apr 2025"
                        value={formData.service_period}
                        onChange={(e) => setFormData({...formData, service_period: e.target.value})}
                        className="bg-slate-50"
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600">
                    Create Invoice
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 text-slate-500 text-xs font-semibold uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-4 text-left">Invoice Number</th>
                  <th className="px-6 py-4 text-left">Date</th>
                  <th className="px-6 py-4 text-left">Due Date</th>
                  <th className="px-6 py-4 text-right">Amount</th>
                  <th className="px-6 py-4 text-center">Status</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-slate-400">
                      No invoices found
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-slate-50/50 border-b border-slate-100">
                      <td className="px-6 py-4 font-semibold text-slate-900">{invoice.invoice_number}</td>
                      <td className="px-6 py-4 text-sm text-slate-700">
                        {format(new Date(invoice.invoice_date), 'MMM dd, yyyy')}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700">
                        {format(new Date(invoice.due_date), 'MMM dd, yyyy')}
                      </td>
                      <td className="px-6 py-4 text-right font-semibold text-slate-900">
                        {formatCurrency(invoice.amount)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${getStatusBadge(invoice.status)}`}>
                          {invoice.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => downloadInvoicePDF(invoice.id, invoice.invoice_number)}
                          className="text-slate-600 hover:text-slate-900 p-2 rounded-lg hover:bg-slate-100"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invoices;
